import React from "react";
import { Text, Flex, Box, Icon } from "orcs-design-system";
import styled from "styled-components";
import themeGet from "@styled-system/theme-get";
import { RAGStyledCard, RAGIconBox } from "./MetricCard/styled";
import {
  metricCardPropTypes,
  getDisplayValue,
  formatTargetDisplay,
  formatDisplayValue,
  buildSubtitleText,
} from "./MetricCard/shared";

const TitleLine = styled(Text)`
  font-size: ${themeGet("fontSizes.s")};
  font-weight: ${themeGet("fontWeights.semibold")};
  color: ${themeGet("colors.black")};
  line-height: ${themeGet("lineHeights.normal")};
  display: block;
`;

const SubText = styled(Text)`
  color: ${themeGet("colors.greyDark")};
  font-size: ${themeGet("fontSizes.xs")};
  margin-top: ${themeGet("space.xxs")};
  display: block;
  line-height: ${themeGet("lineHeights.tight")};
`;

const TargetText = styled(Text)`
  font-weight: ${themeGet("fontWeights.semibold")} !important;
  color: ${themeGet("colors.black")};
  display: inline;
  ${(props) =>
    props.isChanged
      ? `
    text-decoration: underline;
    transition: all 0.3s ease;
  `
      : `
    text-decoration: none;
  `}
`;

const CompactMetricCard = ({
  icon,
  title,
  current,
  currentFormatted,
  target,
  targetFormatted,
  deltaFormatted,
  ragStatus,
  loading = false,
  subtitleText: customSubtitleText,
  targetChanged = false,
  totalOrgLayers = null, // For layers metric: total org layers to show in subtitle
  orgSpan = null, // For span metric: org-wide avg span
  orgManagerToICRatio = null, // For managerToICRatio metric: org-wide ratio
  orgManagementOverhead = null, // For managementOverhead metric: org-wide overhead
}) => {
  const displayValue = getDisplayValue(current, currentFormatted);
  const formattedDisplayValue = formatDisplayValue(displayValue);
  const targetDisplay = formatTargetDisplay(target, targetFormatted);

  // Build subtitle text - for layers, include total org layers if available
  // For other metrics, include org-wide comparison if available
  // Handle newlines in customSubtitleText for direct/indirect breakdown
  let subtitleText = customSubtitleText;
  if (!subtitleText && target !== undefined && deltaFormatted !== undefined) {
    subtitleText = buildSubtitleText(
      displayValue,
      targetDisplay,
      deltaFormatted
    );

    // For layers metric, add total org layers as a second line if available
    if (
      totalOrgLayers !== null &&
      totalOrgLayers !== undefined &&
      title === "Layers"
    ) {
      subtitleText = `${subtitleText} | Org: ${totalOrgLayers}`;
    }
    // For span metric, add org-wide avg span
    else if (
      orgSpan !== null &&
      orgSpan !== undefined &&
      title === "Avg Span"
    ) {
      subtitleText = `${subtitleText} | Org: ${
        orgSpan.current?.toFixed(2) || "N/A"
      }`;
    }
    // For manager to IC ratio, add org-wide ratio
    else if (
      orgManagerToICRatio !== null &&
      orgManagerToICRatio !== undefined &&
      title === "Manager to IC Ratio"
    ) {
      subtitleText = `${subtitleText} | Org: ${
        orgManagerToICRatio.currentFormatted || "N/A"
      }`;
    }
    // For management overhead, add org-wide overhead
    else if (
      orgManagementOverhead !== null &&
      orgManagementOverhead !== undefined &&
      title === "Management Overhead"
    ) {
      subtitleText = `${subtitleText} | Org: ${
        orgManagementOverhead.currentFormatted || "N/A"
      }`;
    }
  }

  // Split subtitle to highlight target if changed
  const renderSubtitle = () => {
    if (target === undefined || deltaFormatted === undefined) {
      return null;
    }

    const textToRender = customSubtitleText || subtitleText;
    if (!textToRender) {
      return null;
    }

    // For metrics with org-wide comparison, split into two lines
    const hasOrgComparison =
      (title === "Layers" &&
        totalOrgLayers !== null &&
        totalOrgLayers !== undefined) ||
      (title === "Avg Span" && orgSpan !== null && orgSpan !== undefined) ||
      (title === "Manager to IC Ratio" &&
        orgManagerToICRatio !== null &&
        orgManagerToICRatio !== undefined) ||
      (title === "Management Overhead" &&
        orgManagementOverhead !== null &&
        orgManagementOverhead !== undefined);

    if (hasOrgComparison) {
      const textParts = textToRender.split(" | ");
      const teamMetricText = textParts[0] || textToRender;
      const orgMetricText = textParts[1] || null;

      return (
        <div>
          {/* First line: Team metric with target highlighting - always bold target VALUES */}
          <SubText as="div">
            {(() => {
              // Match format: "X / Y / Z" - we want to bold Y (the target value, middle value)
              // Split by "/" and identify the middle value
              const metricParts = teamMetricText.split(/\s*\/\s*/);
              if (metricParts.length === 3) {
                // Format: current / target / delta
                return (
                  <>
                    <Text as="span" display="inline">
                      {metricParts[0].trim()}
                    </Text>
                    <Text as="span" display="inline" mx="xs">
                      /
                    </Text>
                    <TargetText
                      as="span"
                      isChanged={targetChanged}
                      display="inline"
                    >
                      {metricParts[1].trim()}
                    </TargetText>
                    <Text as="span" display="inline" mx="xs">
                      /
                    </Text>
                    <Text as="span" display="inline">
                      {metricParts[2].trim()}
                    </Text>
                  </>
                );
              }
              // Fallback for other formats
              return <Text as="span">{teamMetricText}</Text>;
            })()}
          </SubText>
          {/* Second line: Total org metric */}
          {orgMetricText && (
            <SubText as="div" mt="xs">
              {orgMetricText}
            </SubText>
          )}
        </div>
      );
    }

    // Always bold target VALUES for better visibility
    // If target changed, also underline/highlight
    // Match format: "X / Y / Z" - we want to bold Y (the target value, middle value)
    // Handle newlines for direct/indirect breakdown
    // Split by newlines first to handle multi-line subtitles (e.g., direct/indirect breakdown)
    const lines = textToRender.split("\n");

    return (
      <div>
        {lines.map((line, lineIndex) => {
          // Split by "/" and identify the middle value as target
          const metricParts = line.split(/\s*\/\s*/);
          const lineKey = `line-${line
            .slice(0, 20)
            .replace(/\s/g, "-")}-${lineIndex}`;

          if (metricParts.length === 3) {
            // Format: current / target / delta
            return (
              <SubText key={lineKey} as="div" mt={lineIndex > 0 ? "xs" : 0}>
                <Text as="span" display="inline">
                  {metricParts[0].trim()}
                </Text>
                <Text as="span" display="inline" mx="xs">
                  /
                </Text>
                <TargetText
                  as="span"
                  isChanged={targetChanged}
                  display="inline"
                >
                  {metricParts[1].trim()}
                </TargetText>
                <Text as="span" display="inline" mx="xs">
                  /
                </Text>
                <Text as="span" display="inline">
                  {metricParts[2].trim()}
                </Text>
              </SubText>
            );
          }

          // Fallback for other formats
          return (
            <SubText key={lineKey} as="div" mt={lineIndex > 0 ? "xs" : 0}>
              {line}
            </SubText>
          );
        })}
      </div>
    );
  };

  if (loading && current === null) {
    return (
      <RAGStyledCard fluid alternate ragStatus="grey" p="s">
        <Flex alignItems="center" gap="s" width="100%">
          <RAGIconBox ragStatus="grey" flexShrink={0}>
            <Icon icon={icon} fontSize="xs" />
          </RAGIconBox>
          <Box flex="1" minWidth={0}>
            <Text
              fontSize="s"
              fontWeight="semibold"
              color="greyDark"
              display="block"
            >
              {title}
            </Text>
            <Text fontSize="xs" color="greyDark" mt="xs" display="block">
              Loading...
            </Text>
          </Box>
        </Flex>
      </RAGStyledCard>
    );
  }

  if (current === null || current === undefined) {
    return (
      <RAGStyledCard fluid alternate ragStatus="grey" p="s">
        <Flex alignItems="center" gap="s" width="100%">
          <RAGIconBox ragStatus="grey" flexShrink={0}>
            <Icon icon={icon} fontSize="xs" />
          </RAGIconBox>
          <Box flex="1" minWidth={0}>
            <Text
              fontSize="s"
              fontWeight="semibold"
              color="greyDark"
              display="block"
            >
              {title}
            </Text>
            <Text fontSize="xs" color="greyDark" mt="xs" display="block">
              No data available
            </Text>
          </Box>
        </Flex>
      </RAGStyledCard>
    );
  }

  return (
    <RAGStyledCard fluid alternate ragStatus={ragStatus} p="s">
      <Flex alignItems="center" gap="s" width="100%">
        <RAGIconBox ragStatus={ragStatus} flexShrink={0}>
          <Icon icon={icon} fontSize="xs" />
        </RAGIconBox>
        <Box flex="1" minWidth={0}>
          <TitleLine as="div">
            {title}: {formattedDisplayValue}
          </TitleLine>
          {renderSubtitle()}
        </Box>
      </Flex>
    </RAGStyledCard>
  );
};

CompactMetricCard.propTypes = metricCardPropTypes;

export default CompactMetricCard;
